import { Locale } from 'date-fns';
import { isValid } from 'date-fns/isValid';
import { parseISO } from 'date-fns/parseISO';

import { LocalTimeZone } from '../../../utils/LocalTimeZone';
import { logger } from '../../../utils/logger';

import config from '$/config';
import { canonicalTz } from '$/shared/frontend/timezones';
import { RETARGETED_VISITOR_FNAME } from '$/shared/frontend/types';
import EDate from '$/shared/frontend/utils/EDate';
import type { ActionPropertiesHash, ActionPropertyValueFragment } from '$/shared/typesProxy';
import { ActionPropertyType } from '$/shared/typesProxy';

export interface FlattenedEntities {
  [path: string]: string;
}
export interface EmailEntityPaths {
  [path: string]: string;
}

export function flattenEntities(entities: Record<string, unknown>): FlattenedEntities {
  const ob = entities;
  const toReturn: Record<string, unknown> = {};

  for (const i in ob) {
    if (!Object.prototype.hasOwnProperty.call(ob, i)) continue;

    if (typeof ob[i] == 'object' && ob[i] !== null) {
      const flatObject = flattenEntities(ob[i] as Record<string, unknown>);
      for (const x in flatObject) {
        if (!Object.prototype.hasOwnProperty.call(flatObject, x)) continue;
        toReturn[i.toLowerCase() + '.' + x.toLowerCase().replace(' ', '.')] = flatObject[x];
      }
    } else {
      toReturn[i.toLowerCase().replace(' ', '.')] = ob[i];
    }
  }
  return toReturn as FlattenedEntities;
}

// TODO: We will remove `fixups` codes after changed the names on Contentful Email templates
const fixups: { [key: string]: string } = {
  'first.name': 'firstname',
  'last.name': 'lastname',
  'start.time': 'starttime',
  'register.link': 'registrationlink', // fixups codes for URL fields using in ExitRoom settings
  'attendee.replay.link': 'replaylink',
  'all.webinars.link': 'allwebinarslink',
};

/**
 * Standardize attendee's firstName and lastName
 * @param firstName
 * @param lastName
 * @returns
 */
export const standardizeAttendeeNames = (
  firstName?: string | null,
  lastName?: string | null
): { firstName: string; lastName: string; name: string } => {
  if (!firstName || firstName === RETARGETED_VISITOR_FNAME) {
    firstName = '';
  }
  if (!lastName || /^\d+$/.test(lastName)) {
    lastName = '';
  }
  return {
    firstName,
    lastName,
    name: [firstName, lastName].join(' '),
  };
};

/**
 * Separate the fieldName and fallback value from the mention field
 * @param field
 * @returns
 */
const separateFallbackValue = (field: string): { fieldName: string; fallbackValue?: string } => {
  if (field.indexOf('|') === -1) {
    return {
      fieldName: field,
    };
  }
  const [fieldName, ...defaultValue] = field.split('|');
  const fallbackValue = defaultValue.join('|').trim();
  return {
    fieldName: fieldName.trim(),
    fallbackValue,
  };
};

const EMPTY_PLACEHOLDER = '<###>';

export function replacePlaceholders(
  template: string,
  flatEntities: FlattenedEntities,
  timezone: string,
  locale: Locale | undefined,
  entityPaths?: EmailEntityPaths
): string {
  const lookup = (field: string): string => {
    const formatFlags: { relative?: boolean; iso?: boolean } = {};

    const { fieldName, fallbackValue } = separateFallbackValue(field);
    field = fieldName
      .toLowerCase()
      .replace(/\s/g, '.')
      .replace(/(\\)([_+*`])(?!\2)/g, (_match: string, _p1: string, p2: string) => p2); // Remove `\` before some special characters of custom fields

    // TODO: We will remove `fixups` codes after changed the names on Contentfull Email templates
    Object.keys(fixups).forEach((key: string) => {
      field = field.replace(key, fixups[key]);
    });

    if (field.endsWith('.left')) {
      field = field.substr(0, field.length - '.left'.length);
      formatFlags.relative = true;
    }

    if (field.endsWith('.iso')) {
      field = field.substr(0, field.length - '.iso'.length);
      formatFlags.iso = true;
    }

    let value = flatEntities[field];
    if (entityPaths && entityPaths[field]) {
      value = flatEntities[entityPaths[field]];
    }

    // The value is empty and has a fallback value
    if (!value && fallbackValue) {
      return fallbackValue;
    }

    if (value === null || value === undefined) {
      logger.warn(`ERROR! Filling "${template}" needs unknown field: "${field}"`);

      if (config.isProduction) {
        return EMPTY_PLACEHOLDER;
      }

      return ` { ${field} } `;
    }

    // DOT NOT format date if value is a string number (e.g. new Date('123000') is a valid date, but we don't want to format it as a date)
    if (!isNaN(Number(value))) {
      return value;
    }

    const maybeDate = parseISO(value);
    if (isValid(maybeDate)) {
      const date = new EDate(maybeDate, timezone);
      if (formatFlags.relative) {
        return date.formatDistanceToNow({ locale });
      }
      if (formatFlags.iso) {
        return date.toISOString();
      }
      return date.format('PPPPpp zz', { locale });
    }

    return value || EMPTY_PLACEHOLDER;
  };

  // Fields can contain characters: \_ \+ \* \` or |
  // `|` is the character separate between fieldName and fallback value
  const placeholderRegex = /[<{]{1,2}\s*([\w\s.\\_\\+\\*\\`|]*\w+)\s*[>}]{1,2}/g;

  let filled = '';
  let lastIndex = 0;

  let matches;
  while ((matches = placeholderRegex.exec(template)) !== null) {
    filled += template.substring(lastIndex, matches.index) + lookup(matches[1]);
    lastIndex = placeholderRegex.lastIndex;
  }

  filled += template.substring(lastIndex);

  // Remove the placeholder string
  filled = filled.replace(new RegExp(`[^\\S\\n\\r]*${EMPTY_PLACEHOLDER}`, 'g'), '');

  return filled;
}

interface AttendeeUrls {
  replay: string;
}

interface AttendeeEntity {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  locale?: string;
  urls?: AttendeeUrls;
  timezone?: string | null;
}

interface EWebinarSetUrls {
  setUrl: string;
  shortUrl: string;
  replayRegistration: string;
  registration: string;
}

interface EWebinarEntity {
  id: string;
  title: string;
  set: {
    id: string;
    urls: EWebinarSetUrls;
  };
}

const stringifyPropertyValue = (
  prop: ActionPropertyValueFragment,
  timezone?: string,
  locale?: Locale
): string => {
  const v = prop.value;
  if (!v) {
    return '';
  }
  if (Array.isArray(v)) {
    return v.join(', ');
  }

  switch (prop.type) {
    case ActionPropertyType.Boolean:
      return v ? 'true' : 'false';

    case ActionPropertyType.DateTime:
      return !timezone
        ? String(v) /* Assume ISO */
        : new EDate(new Date(v.toString()), timezone).format('P pp zzz', { locale: locale });

    case ActionPropertyType.Number:
      return Number(v).toString();
  }

  if (typeof v === 'object') {
    logger.error('Error formatting property', { prop });
    return '';
  }
  return String(v);
};

export const stringifyActionProperties = (
  actionProperties?: ActionPropertiesHash | null,
  timezone?: string,
  locale?: Locale
): Record<string, string> => {
  let props: Record<string, string> = {};
  try {
    props = Object.values(actionProperties ?? {}).reduce<Record<string, string>>(
      (propsHash, prop) => {
        if (prop?.property) {
          const v = stringifyPropertyValue(prop, timezone, locale);
          propsHash[prop.property] = v;
        }
        return propsHash;
      },
      {}
    );
  } catch (error) {
    logger.error('stringifyActionProperties -> map properties', error);
  }
  return props;
};

export const injectAttendeeFieldsBasic = (
  message: string,
  locale: Locale | undefined,
  attendee: AttendeeEntity,
  ewebinar?: EWebinarEntity,
  actionProperties?: ActionPropertiesHash | null
): string => {
  // standardize Attendee names for action properties
  const { firstName, lastName, name } = standardizeAttendeeNames(
    attendee.firstName,
    attendee.lastName
  );

  const timezone = canonicalTz(attendee.timezone) ?? LocalTimeZone.name;

  const props: Record<string, unknown> = stringifyActionProperties(actionProperties);

  const entities: Record<string, unknown> = {
    ...props,
    firstName,
    lastName,
    name,
    to: {
      name: [attendee.firstName, attendee.lastName].join(' '),
      firstName: attendee.firstName,
      lastName: attendee.lastName,
    },
    attendee: {
      id: attendee.id,
      firstName: attendee.firstName,
      lastName: attendee.lastName,
      email: attendee.email,
      urls: attendee.urls,
    },
  };
  if (ewebinar) {
    entities.ewebinar = {
      id: ewebinar.id,
      title: ewebinar.title,
      set: {
        id: ewebinar.set.id,
        urls: ewebinar.set.urls,
      },
    };
  }
  try {
    return replacePlaceholders(message, flattenEntities(entities), timezone, locale, undefined);
  } catch (error) {
    logger.error('injectAttendeeFieldsBasic', { error, message, entities });
    return message;
  }
};
